import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import type { CalendarAppointment, CalendarBlock, PartKind } from '../../lib/calendar';
import { useT, type TKey } from '../../lib/i18n';
import { hhmm } from '../../lib/time';
import { formatEuro } from '../forms';

/** Pixels per minute: 15 minutes = 24px, readable at arm's length. */
export const PX = 1.6;
const SNAP = 5;

export type MinuteRange = { start: number; end: number };

export type GridColumn = {
  key: string;
  date: string;
  staffId: string;
  header: ReactNode;
  color: string;
  /** When this person works, in minutes of the day. Everything else is hatched. */
  working: MinuteRange[];
  closedLabel?: string;
  isToday: boolean;
};

export type GridItem = {
  appointment: CalendarAppointment;
  columnKey: string;
  start: number;
  end: number;
  parts: Array<{ kind: PartKind; start: number; end: number }>;
};

export type GridBlock = { key: string; columnKey: string; start: number; end: number; block: CalendarBlock };

export type MoveRequest = { item: GridItem; column: GridColumn; start: number; duration: number };

/** The new appointment (or blocked time) being made, drawn in place while the panel is open. */
export type GridPreview = { columnKey: string; start: number; end: number; label: string };

type Selection = { columnKey: string; anchor: number; current: number };
/** New appointments snap to quarter hours, like a paper agenda. */
const SLOT = 15;
const selectionRange = (s: Selection) => ({ start: Math.min(s.anchor, s.current), end: Math.max(s.anchor, s.current) + SLOT });

type Drag = {
  item: GridItem;
  mode: 'move' | 'resize';
  columnKey: string;
  start: number;
  duration: number;
};

const draggable = (a: CalendarAppointment) => a.canEdit && (a.status === 'booked' || a.status === 'client_confirmed' || a.status === 'requested');

/** Side-by-side lanes for appointments that overlap (only after "Toch inplannen"). */
function layoutLanes(items: GridItem[]) {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end);
  const out = new Map<string, { lane: number; lanes: number }>();
  let cluster: GridItem[] = [];
  let clusterEnd = -1;
  let laneEnds: number[] = [];
  const flush = () => {
    for (const it of cluster) out.get(it.appointment.id)!.lanes = laneEnds.length;
    cluster = [];
    laneEnds = [];
  };
  for (const it of sorted) {
    if (it.start >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= it.start);
    if (lane === -1) lane = laneEnds.push(it.end) - 1;
    else laneEnds[lane] = it.end;
    out.set(it.appointment.id, { lane, lanes: 1 });
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  flush();
  return out;
}

export function TimeGrid({
  columns,
  items,
  blocks,
  startMin,
  endMin,
  nowMin,
  minColumnWidth = 150,
  preview,
  onSlot,
  onOpen,
  onOpenBlock,
  onMove,
  onSwipe,
}: {
  columns: GridColumn[];
  items: GridItem[];
  blocks: GridBlock[];
  startMin: number;
  endMin: number;
  nowMin: number | null;
  minColumnWidth?: number;
  preview?: GridPreview | null;
  /** A click on empty time (end = null) or a dragged range. */
  onSlot: (column: GridColumn, start: number, end: number | null) => void;
  onOpen: (appointment: CalendarAppointment) => void;
  onOpenBlock: (block: CalendarBlock) => void;
  onMove: (move: MoveRequest) => void;
  onSwipe?: (direction: 1 | -1) => void;
}) {
  const t = useT();
  const scrollRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef(new Map<string, HTMLDivElement>());
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  dragRef.current = drag;
  /** A drag ends with a click on the column underneath; ignore that one. */
  const suppressSlotUntil = useRef(0);
  const [selection, setSelection] = useState<Selection | null>(null);
  const selectionRef = useRef<Selection | null>(null);
  selectionRef.current = selection;
  /** Mouse hover over empty time: shows which quarter a click would book. */
  const [hover, setHover] = useState<{ columnKey: string; minute: number } | null>(null);
  const height = (endMin - startMin) * PX;
  // Columns of 260px or more have room for the end time, price and note on an appointment.
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setWide((el.clientWidth - 52) / Math.max(1, columns.length) >= 260);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [columns.length]);

  // Start scrolled to just before now (or the start of the working day).
  const scrolled = useRef(false);
  useLayoutEffect(() => {
    if (scrolled.current || !scrollRef.current) return;
    const firstWork = Math.min(...columns.flatMap((c) => c.working.map((w) => w.start)), endMin);
    const target = nowMin !== null && nowMin > startMin && nowMin < endMin ? nowMin - 60 : firstWork - 30;
    scrollRef.current.scrollTop = Math.max(0, (target - startMin) * PX);
    scrolled.current = true;
  }, [columns, nowMin, startMin, endMin]);

  // While dragging on a touch screen, stop the page from scrolling.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const block = (e: TouchEvent) => {
      if (dragRef.current || selectionRef.current) e.preventDefault();
    };
    el.addEventListener('touchmove', block, { passive: false });
    return () => el.removeEventListener('touchmove', block);
  }, []);

  // Swipe left/right to change day (phones).
  const swipe = useRef<{ x: number; y: number; t: number } | null>(null);

  const minuteAt = (clientY: number, columnKey: string) => {
    const el = columnRefs.current.get(columnKey);
    if (!el) return startMin;
    return startMin + (clientY - el.getBoundingClientRect().top) / PX;
  };
  const columnAt = (clientX: number) => {
    for (const [key, el] of columnRefs.current) {
      const r = el.getBoundingClientRect();
      if (clientX >= r.left && clientX < r.right) return key;
    }
    return undefined;
  };
  const snap = (m: number) => Math.round(m / SNAP) * SNAP;

  function startPointer(e: ReactPointerEvent, item: GridItem, mode: Drag['mode']) {
    if (e.button !== 0) return;
    e.stopPropagation();
    const canDrag = draggable(item.appointment);
    const x0 = e.clientX;
    const y0 = e.clientY;
    const touch = e.pointerType === 'touch';
    const grabOffset = minuteAt(y0, item.columnKey) - item.start;
    let active = false;
    let timer: number | undefined;
    let removeKey = () => {};

    const activate = () => {
      if (!canDrag) return;
      active = true;
      if (touch && 'vibrate' in navigator) navigator.vibrate?.(10);
      setDrag({ item, mode, columnKey: item.columnKey, start: item.start, duration: item.end - item.start });
    };
    if (touch) timer = window.setTimeout(activate, 350);

    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - x0;
      const dy = ev.clientY - y0;
      if (!active) {
        if (touch) {
          if (Math.hypot(dx, dy) > 8) cleanup(); // it's a scroll, not a long-press
          return;
        }
        if (Math.hypot(dx, dy) > 4) activate();
        if (!active) return;
      }
      autoScroll(ev.clientY);
      setDrag((d) => {
        if (!d) return d;
        if (d.mode === 'resize') {
          const duration = Math.max(SNAP, snap(minuteAt(ev.clientY, d.columnKey) - d.start));
          return { ...d, duration: Math.min(duration, endMin - d.start) };
        }
        const columnKey = columnAt(ev.clientX) ?? d.columnKey;
        const start = Math.min(Math.max(startMin, snap(minuteAt(ev.clientY, columnKey) - grabOffset)), endMin - d.duration);
        return { ...d, columnKey, start };
      });
    };
    const up = () => {
      const d = dragRef.current;
      cleanup();
      // A plain tap or click opens the appointment through the click event.
      if (!active) return;
      suppressSlotUntil.current = Date.now() + 400;
      setDrag(null);
      if (!d) return;
      const changed = d.start !== item.start || d.columnKey !== item.columnKey || d.duration !== item.end - item.start;
      const column = columns.find((c) => c.key === d.columnKey);
      if (changed && column) onMove({ item, column, start: d.start, duration: d.duration });
    };
    const cancel = () => {
      cleanup();
      setDrag(null);
      suppressSlotUntil.current = Date.now() + 400;
    };
    function cleanup() {
      window.clearTimeout(timer);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      removeKey();
    }
    const onKey = (ev: KeyboardEvent) => ev.key === 'Escape' && cancel();
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', onKey);
    removeKey = () => window.removeEventListener('keydown', onKey);
  }

  /**
   * Press and drag on empty time to select a range (Google Calendar style).
   * Touch: long-press first, so a normal swipe still scrolls.
   */
  function startSelection(e: ReactPointerEvent, column: GridColumn) {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target !== e.currentTarget && !target.dataset.slot) return;
    const x0 = e.clientX;
    const y0 = e.clientY;
    const touch = e.pointerType === 'touch';
    const slotAt = (y: number) => Math.min(endMin - SLOT, Math.max(startMin, Math.floor(minuteAt(y, column.key) / SLOT) * SLOT));
    const anchor = slotAt(y0);
    let active = false;
    let timer: number | undefined;
    const activate = () => {
      active = true;
      if (touch && 'vibrate' in navigator) navigator.vibrate?.(10);
      setSelection({ columnKey: column.key, anchor, current: anchor });
    };
    if (touch) timer = window.setTimeout(activate, 350);

    const move = (ev: PointerEvent) => {
      if (!active) {
        const dist = Math.hypot(ev.clientX - x0, ev.clientY - y0);
        if (touch) {
          if (dist > 8) cleanup();
          return;
        }
        if (dist > 4) activate();
        if (!active) return;
      }
      autoScroll(ev.clientY);
      setSelection((s) => (s ? { ...s, current: slotAt(ev.clientY) } : s));
    };
    const up = () => {
      const s = selectionRef.current;
      cleanup();
      if (!active) return; // a plain click: the column's click handler opens the panel
      suppressSlotUntil.current = Date.now() + 400;
      setSelection(null);
      if (s) {
        const r = selectionRange(s);
        onSlot(column, r.start, r.end);
      }
    };
    const cancel = () => {
      cleanup();
      setSelection(null);
      suppressSlotUntil.current = Date.now() + 400;
    };
    const onKey = (ev: KeyboardEvent) => ev.key === 'Escape' && cancel();
    function cleanup() {
      window.clearTimeout(timer);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', onKey);
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', onKey);
  }

  function autoScroll(clientY: number) {
    const el = scrollRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (clientY < r.top + 40) el.scrollTop -= 12;
    else if (clientY > r.bottom - 40) el.scrollTop += 12;
  }

  const hours: number[] = [];
  for (let m = Math.ceil(startMin / 60) * 60; m < endMin; m += 60) hours.push(m);

  return (
    <div
      ref={scrollRef}
      className="relative max-h-[calc(100dvh-240px)] min-h-[360px] overflow-auto rounded-[var(--radius-card)] border border-line bg-surface md:max-h-[calc(100dvh-170px)]"
      onTouchStart={(e) => {
        const p = e.touches[0];
        if (p && e.touches.length === 1) swipe.current = { x: p.clientX, y: p.clientY, t: Date.now() };
      }}
      onTouchEnd={(e) => {
        const s = swipe.current;
        const p = e.changedTouches[0];
        swipe.current = null;
        if (!s || !p || !onSwipe || dragRef.current || selectionRef.current) return;
        const dx = p.clientX - s.x;
        const dy = p.clientY - s.y;
        if (Math.abs(dx) > 70 && Math.abs(dy) < 50 && Date.now() - s.t < 600) onSwipe(dx < 0 ? 1 : -1);
      }}
    >
      <div className="grid" style={{ gridTemplateColumns: `52px repeat(${columns.length}, minmax(${minColumnWidth}px, 1fr))` }}>
        {/* Header row */}
        <div className="sticky left-0 top-0 z-30 border-b border-line bg-surface" />
        {columns.map((c) => (
          <div key={c.key} className="sticky top-0 z-20 flex min-h-12 items-center justify-center border-b border-l border-line bg-surface px-2 py-1.5 text-center">
            {c.header}
          </div>
        ))}

        {/* Time gutter */}
        <div className="sticky left-0 z-10 bg-surface" style={{ height }}>
          {hours.map((m) => (
            <div key={m} className="absolute right-2 -translate-y-1/2 text-xs tabular-nums text-muted" style={{ top: (m - startMin) * PX }}>
              {m > startMin ? hhmm(m) : ''}
            </div>
          ))}
        </div>

        {/* Columns */}
        {columns.map((c) => {
          const colItems = items.filter((i) => i.columnKey === c.key && i.appointment.id !== drag?.item.appointment.id);
          const lanes = layoutLanes(colItems);
          return (
            <div
              key={c.key}
              ref={(el) => {
                if (el) columnRefs.current.set(c.key, el);
                else columnRefs.current.delete(c.key);
              }}
              data-column={c.key}
              className="relative cursor-pointer border-l border-line"
              style={{
                height,
                backgroundColor: 'var(--color-canvas)',
                backgroundImage: 'repeating-linear-gradient(135deg, var(--color-subtle) 0 6px, transparent 6px 12px)',
              }}
              onPointerDown={(e) => {
                setHover(null);
                startSelection(e, c);
              }}
              onPointerMove={(e) => {
                if (e.pointerType !== 'mouse' || dragRef.current || selectionRef.current) return;
                const target = e.target as HTMLElement;
                if (target !== e.currentTarget && !target.dataset.slot) {
                  if (hover) setHover(null);
                  return;
                }
                const minute = Math.max(startMin, Math.floor(minuteAt(e.clientY, c.key) / SLOT) * SLOT);
                if (hover?.columnKey !== c.key || hover.minute !== minute) setHover({ columnKey: c.key, minute });
              }}
              onPointerLeave={() => setHover(null)}
              onClick={(e) => {
                if (e.target !== e.currentTarget && !(e.target as HTMLElement).dataset.slot) return;
                if (Date.now() < suppressSlotUntil.current) return;
                const minute = Math.floor(minuteAt(e.clientY, c.key) / SLOT) * SLOT;
                onSlot(c, Math.max(startMin, minute), null);
              }}
            >
              {c.working.map((w, i) => (
                <div key={i} data-slot="1" className="absolute inset-x-0 bg-surface" style={{ top: (w.start - startMin) * PX, height: (w.end - w.start) * PX }} />
              ))}
              {/* Hour and half-hour lines */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  backgroundImage: `linear-gradient(to bottom, var(--color-line) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in srgb, var(--color-line) 45%, transparent) 1px, transparent 1px)`,
                  backgroundSize: `100% ${60 * PX}px, 100% ${30 * PX}px`,
                  backgroundPositionY: `${((Math.ceil(startMin / 60) * 60 - startMin) * PX) % (60 * PX)}px, ${((Math.ceil(startMin / 30) * 30 - startMin) * PX) % (30 * PX)}px`,
                }}
              />
              {c.closedLabel && (
                <div className="pointer-events-none absolute inset-x-0 top-3 text-center text-sm font-medium text-muted">{c.closedLabel}</div>
              )}

              {blocks
                .filter((b) => b.columnKey === c.key)
                .map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenBlock(b.block);
                    }}
                    className="absolute inset-x-1 z-[1] flex items-start overflow-hidden rounded-md border border-line bg-subtle px-2 py-0.5 text-left text-xs font-medium text-muted"
                    style={{ top: (b.start - startMin) * PX, height: Math.max(14, (b.end - b.start) * PX) }}
                  >
                    {t(`reason.${b.block.reason}` as TKey)}
                    {b.block.note ? ` · ${b.block.note}` : ''}
                  </button>
                ))}

              {colItems.map((it) => {
                const l = lanes.get(it.appointment.id) ?? { lane: 0, lanes: 1 };
                return (
                  <AppointmentCard
                    key={it.appointment.id}
                    item={it}
                    color={c.color}
                    lane={l.lane}
                    lanes={l.lanes}
                    wide={wide}
                    startMin={startMin}
                    onPointerDown={startPointer}
                    onOpen={(a) => Date.now() >= suppressSlotUntil.current && onOpen(a)}
                  />
                );
              })}

              {drag && drag.columnKey === c.key && (
                <AppointmentCard
                  item={{ ...drag.item, columnKey: c.key, start: drag.start, end: drag.start + drag.duration, parts: shiftParts(drag.item, drag.start, drag.duration) }}
                  color={c.color}
                  lane={0}
                  lanes={1}
                  startMin={startMin}
                  ghost
                />
              )}

              {hover && hover.columnKey === c.key && !selection && !drag && !(preview && preview.columnKey === c.key) && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-[3px] z-[2] rounded-md bg-accent-soft/70 px-1.5 text-xs font-semibold leading-[22px] text-accent-strong"
                  style={{ top: (hover.minute - startMin) * PX + 1, height: SLOT * PX - 2 }}
                >
                  {hhmmLabel(hover.minute)}
                </div>
              )}
              {selection && selection.columnKey === c.key && <NewBlock range={selectionRange(selection)} startMin={startMin} />}
              {!selection && preview && preview.columnKey === c.key && <NewBlock range={preview} startMin={startMin} label={preview.label} />}

              {c.isToday && nowMin !== null && nowMin >= startMin && nowMin <= endMin && (
                <div className="pointer-events-none absolute inset-x-0 z-[5] border-t-2 border-accent" style={{ top: (nowMin - startMin) * PX }}>
                  <span className="absolute -left-1.5 -top-[6px] size-2.5 rounded-full bg-accent ring-2 ring-surface" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The block being drawn or the appointment being made: accent outline with its times. */
function NewBlock({ range, startMin, label }: { range: { start: number; end: number }; startMin: number; label?: string }) {
  const minutes = range.end - range.start;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-[3px] z-[7] overflow-hidden rounded-md border-2 border-accent bg-accent-soft/90 px-1.5 py-0.5 text-[13px] leading-tight text-accent-strong shadow-md"
      style={{ top: (range.start - startMin) * PX + 1, height: Math.max(18, minutes * PX - 2) }}
    >
      <span className="font-semibold tabular-nums">
        {hhmmLabel(range.start)} – {hhmmLabel(range.end)}
      </span>
      {label && minutes > 20 && <span className="block truncate">{label}</span>}
    </div>
  );
}

function shiftParts(item: GridItem, start: number, duration: number) {
  const delta = start - item.start;
  const grow = duration - (item.end - item.start);
  const last = item.parts.map((p) => p.kind).lastIndexOf('active');
  return item.parts.map((p, i) => ({ kind: p.kind, start: p.start + delta + (i > last ? grow : 0), end: p.end + delta + (i >= last ? grow : 0) }));
}

function AppointmentCard({
  item,
  color,
  lane,
  lanes,
  wide = false,
  startMin,
  ghost = false,
  onPointerDown,
  onOpen,
}: {
  item: GridItem;
  color: string;
  lane: number;
  lanes: number;
  wide?: boolean;
  startMin: number;
  ghost?: boolean;
  onPointerDown?: (e: ReactPointerEvent, item: GridItem, mode: Drag['mode']) => void;
  onOpen?: (a: CalendarAppointment) => void;
}) {
  const t = useT();
  const a = item.appointment;
  const minutes = item.end - item.start;
  const compact = minutes <= 20;
  // A wide block on its own (day view on a big screen) has room for the end time, price and note.
  const roomy = !compact && lanes === 1 && wide;
  const faded = a.status === 'no_show';
  const width = 100 / lanes;
  const buffer = item.parts.filter((p) => p.kind === 'buffer_after' || p.kind === 'buffer_before');
  const marks = [a.source === 'online' && 'online', a.newClient && 'new', a.alert && 'alert', a.requested && 'requested'].filter(Boolean) as Mark[];
  const name = a.customerName ?? (a.source === 'walk_in' ? t('cal.walkIn') : t('cal.noClient'));

  return (
    <>
      {buffer.map((p, i) => (
        <div
          key={i}
          aria-hidden
          className="pointer-events-none absolute z-[2]"
          style={{
            top: (p.start - startMin) * PX,
            height: (p.end - p.start) * PX,
            left: `calc(${lane * width}% + 3px)`,
            width: `calc(${width}% - 6px)`,
            backgroundImage: `repeating-linear-gradient(135deg, ${color}33 0 4px, transparent 4px 8px)`,
          }}
        />
      ))}
      <button
        type="button"
        aria-label={`${hhmmLabel(item.start)} ${name}, ${a.services.join(', ')}`}
        onPointerDown={(e) => onPointerDown?.(e, item, 'move')}
        onClick={(e) => {
          e.stopPropagation();
          onOpen?.(a);
        }}
        className={`absolute z-[3] flex select-none flex-col justify-start overflow-hidden rounded-[10px] border-l-[3px] text-left text-[13px] leading-tight text-ink shadow-[0_1px_2px_rgb(2_53_42/0.08)] transition-shadow hover:shadow-[0_3px_10px_rgb(2_53_42/0.12)] [-webkit-touch-callout:none] ${ghost ? 'z-[6] opacity-95 shadow-lg ring-2 ring-accent-strong' : ''} ${faded ? 'opacity-60' : ''}`}
        style={{
          top: (item.start - startMin) * PX + 1,
          height: Math.max(18, minutes * PX - 2),
          left: `calc(${lane * width}% + 3px)`,
          width: `calc(${width}% - 6px)`,
          borderLeftColor: color,
          backgroundColor: `color-mix(in srgb, ${color} 13%, var(--color-surface))`,
          touchAction: 'pan-y',
        }}
      >
        {item.parts
          .filter((p) => p.kind === 'processing')
          .map((p, i) => (
            <span
              key={i}
              aria-hidden
              className="pointer-events-none absolute inset-x-0"
              style={{
                top: (p.start - item.start) * PX,
                height: (p.end - p.start) * PX,
                backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.8) 0 6px, rgba(255,255,255,0.5) 6px 12px)',
              }}
            />
          ))}
        <span className={`relative block px-1.5 ${compact ? 'truncate py-px' : 'py-1'}`}>
          <span className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate">
              <span className="font-medium tabular-nums text-muted">
                {hhmmLabel(item.start)}
                {(ghost || roomy) && `–${hhmmLabel(item.end)}`}
              </span>{' '}
              <span className="font-semibold">{name}</span>
              {marks.length > 0 && (
                <span className="ml-1 inline-flex translate-y-[1px] items-center gap-0.5 align-baseline">
                  {marks.map((m) => (
                    <MarkIcon key={m} mark={m} />
                  ))}
                </span>
              )}
            </span>
            {roomy && a.priceCents > 0 && <span className="shrink-0 tabular-nums text-muted">{formatEuro(a.priceCents).replace(/,00$/, '')}</span>}
          </span>
          {!compact && <span className="block truncate text-muted">{a.services.join(' + ')}</span>}
          {roomy && minutes >= 45 && a.note && <span className="mt-0.5 block truncate italic text-muted">{a.note}</span>}
          {a.status === 'no_show' && <span className="block text-xs font-semibold text-danger">{t('status.no_show')}</span>}
        </span>
        {draggable(a) && !ghost && (
          <span
            aria-hidden
            onPointerDown={(e) => onPointerDown?.(e, item, 'resize')}
            className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize"
          />
        )}
      </button>
    </>
  );
}

const hhmmLabel = (m: number) => hhmm(((m % 1440) + 1440) % 1440);

type Mark = 'online' | 'new' | 'alert' | 'requested';

/** Tiny drawn symbols on an appointment: booked online, new client, a warning, client chose this person. */
function MarkIcon({ mark }: { mark: Mark }) {
  const t = useT();
  const label = { online: t('cal.online'), new: t('tag.new'), alert: t('cal.alert'), requested: t('cal.requested') }[mark];
  const path = {
    online: <><circle cx="8" cy="8" r="6" /><path d="M2 8h12M8 2c2 2.2 2 9.8 0 12M8 2c-2 2.2-2 9.8 0 12" /></>,
    new: <path d="M8 2.2l1.5 3.6 3.8.3-2.9 2.5.9 3.8L8 10.4l-3.3 2 .9-3.8-2.9-2.5 3.8-.3L8 2.2Z" />,
    alert: <><path d="M8 2.5 14 13H2L8 2.5Z" /><path d="M8 6.5v3M8 11.2v.1" /></>,
    requested: <><circle cx="8" cy="5.5" r="2.5" /><path d="M3.5 13.5c.6-2.4 2.4-3.6 4.5-3.6s3.9 1.2 4.5 3.6" /></>,
  }[mark];
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={mark === 'alert' ? 'text-warning' : 'text-muted'} role="img" aria-label={label}>
      <title>{label}</title>
      {path}
    </svg>
  );
}
